import { useEffect, useState } from "react";
import { useParams, useLocation, useOutletContext } from "react-router-dom";
import {
  Box,
  Card,
  Typography,
  Divider,
  CircularProgress,
  Stack,
  TextField,
  Button,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import jsPDF from "jspdf";
import useGetPoemSurveyAnswers from "../../hooks/useGetSurveyAnswers";
import { buildPoemFooterText, renderPoemSurveysToPdf } from "../../utils/poemPdfUtils";

const disabledFieldSx = {
  "& .MuiInputBase-input.Mui-disabled": {
    WebkitTextFillColor: "#000000",
  },
};

const PoemDetail = () => {
  const { _id_, caseId } = useParams();
  const location = useLocation();
  const [isExporting, setIsExporting] = useState(false);

  const visitName =
    location.state?.visitName || new URLSearchParams(location.search).get("visitName");
  const inappCompletedDate = location.state?.inappCompletedDate;
  const { infoData, setActive, setBreadcrumbTitle } = useOutletContext();
  useEffect(() => {
    setActive("poem");
    setBreadcrumbTitle("POEMs / ePRO / eDiary");
  }, [setActive, setBreadcrumbTitle]);

  const { data: surveyList = [], isLoading } = useGetPoemSurveyAnswers({
    patientId: _id_,
    sessionId: caseId,
  });

  const handleExportPdf = () => {
    setIsExporting(true);
    try {
      const pdf = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4" });
      const footerText = buildPoemFooterText({
        trialName: infoData?.trial_name,
        visitName,
        siteId: infoData?.site_id,
        participantId: infoData?.participant_id,
        visitDate: inappCompletedDate,
      });
      renderPoemSurveysToPdf(pdf, { surveyList, footerText });
      pdf.save(`POEM_${visitName || caseId || "export"}.pdf`);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Box p={3}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="subtitle1" fontWeight={600}>
          Visit Name: {visitName || "-"}
        </Typography>

        {!isLoading && surveyList.length > 0 && (
          <Button
            variant="outlined"
            size="small"
            startIcon={<DownloadIcon />}
            onClick={handleExportPdf}
            disabled={isExporting}
          >
            {isExporting ? "Exporting..." : "Export as PDF"}
          </Button>
        )}
      </Stack>

      <Card sx={{ p: 3 }}>
        {isLoading ? (
          <Box display="flex" justifyContent="center" py={6}>
            <CircularProgress />
          </Box>
        ) : surveyList.length > 0 ? (
          <Stack spacing={5}>
            {surveyList.map((survey, surveyIndex) => (
              <Box key={surveyIndex}>
                {/* Survey Name */}
                <Typography variant="h6" fontWeight={600} mb={1}>
                  {survey.survey_name}
                </Typography>

                <Divider sx={{ mb: 3 }} />

                {/* Questions */}
                <Stack spacing={3}>
                  {survey.answers.map((question, index) => (
                    <Stack spacing={2} key={index}>
                      <TextField
                        label="Question"
                        value={question?.question || ""}
                        multiline
                        disabled
                        fullWidth
                        sx={disabledFieldSx}
                      />

                      <TextField
                        label="Answer"
                        value={
                          question?.is_multi_choice
                            ? question?.answer?.map((a) => a?.choice).join(", ")
                            : question?.answer || ""
                        }
                        multiline
                        disabled
                        fullWidth
                        sx={disabledFieldSx}
                      />

                      {question?.extra_text && (
                        <TextField
                          label="Extra Text"
                          value={question.extra_text}
                          multiline
                          disabled
                          fullWidth
                          sx={disabledFieldSx}
                        />
                      )}
                    </Stack>
                  ))}
                </Stack>
              </Box>
            ))}
          </Stack>
        ) : (
          <Typography textAlign="center" color="text.secondary">
            POEM not available
          </Typography>
        )}
      </Card>
    </Box>
  );
};

export default PoemDetail;
